import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUiStore } from '../../store/uiStore';
import { ApiError } from '../../api/functionsClient';
import { Spinner } from '../../components/Spinner';

export function Register() {
  const { register } = useAuth();
  const pushToast = useUiStore((s) => s.pushToast);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      pushToast('error', 'Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password, displayName);
      setDone(true);
      pushToast('success', 'Account created — check your email to verify.');
    } catch (err) {
      const message =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Registration failed';
      pushToast('error', message);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-slate-100">Almost there</h2>
        <p className="text-sm text-slate-300">
          We&apos;ve sent a verification link to{' '}
          <span className="font-semibold text-gold">{email}</span>. Please check
          your inbox to verify your email before signing in.
        </p>
        <Link to="/login" className="btn-gold w-full">
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-100">Create account</h2>

      <div>
        <label className="label" htmlFor="register-name">
          Display name
        </label>
        <input
          id="register-name"
          type="text"
          autoComplete="nickname"
          className="input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
      </div>

      <div>
        <label className="label" htmlFor="register-email">
          Email
        </label>
        <input
          id="register-email"
          type="email"
          autoComplete="email"
          className="input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      <div>
        <label className="label" htmlFor="register-password">
          Password
        </label>
        <input
          id="register-password"
          type="password"
          autoComplete="new-password"
          className="input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={6}
          required
        />
      </div>

      <div>
        <label className="label" htmlFor="register-confirm">
          Confirm password
        </label>
        <input
          id="register-confirm"
          type="password"
          autoComplete="new-password"
          className="input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          minLength={6}
          required
        />
      </div>

      <button type="submit" className="btn-gold w-full" disabled={submitting}>
        {submitting ? <Spinner /> : 'Create account'}
      </button>

      <p className="text-center text-sm text-slate-400">
        Already have an account?{' '}
        <Link to="/login" className="hover:text-gold">
          Sign in
        </Link>
      </p>
    </form>
  );
}
